/**
 * This patch removes corrupt dynamic list filters (node.data.dyn.query.fields). These can be for example a corrupt
 * Date field filters or filters where `fields` is an empty object.
 * NOTE: query can contain also other properties that we are NOT supposed to delete even if we delete invalid filters.
 *
 * The filter (node.data.dyn.query.fields) structure is the following.
 *
 * {
 *   query: {
 *     fields: {
 *       name: {
 *         ne: "Foo"
 *       },
 *       age: {
 *         gte: 21
 *       }
 *     },
 *     sort: [
 *       "-name"
 *     ],
 *     limit: 20,
 *     offset: 10
 *   }
 * }
 */

import type {Context} from '../types';

export default (context: Context) => {
  const {
    node: {jsonNode},
    extraContext: {collections},
  } = context;
  const dynData = jsonNode.data && jsonNode.data.dyn;
  const isDynamicListWrapper =
    dynData && dynData.type && dynData.type === 'wrapper';
  const fields = isDynamicListWrapper && dynData.query && dynData.query.fields;
  const listCollectionId =
    isDynamicListWrapper && dynData.collection && dynData.collection.id;
  const listCollection =
    listCollectionId && collections && collections[listCollectionId];
  const fieldKeys = fields && Object.keys(fields);
  const collectionFields = listCollection && listCollection.fields;
  const collectionDateFields =
    collectionFields &&
    collectionFields.length > 0 &&
    collectionFields.filter(isDate);
  const collectionDateFieldSlugs =
    collectionDateFields && collectionDateFields.map(getSlug);
  const filterDateFieldSlugs =
    fieldKeys &&
    collectionDateFieldSlugs &&
    fieldKeys.filter(partOf(collectionDateFieldSlugs));
  const corruptFilterDateFieldSlugs =
    filterDateFieldSlugs &&
    filterDateFieldSlugs.filter((field: any) => {
      const operations = fields && fields[field];
      return (
        (operations.gte && typeof operations.gte !== 'string') ||
        (operations.lte && typeof operations.lte !== 'string')
      );
    });

  const patchNeeded =
    corruptFilterDateFieldSlugs && corruptFilterDateFieldSlugs.length > 0;

  return {
    needed: patchNeeded,
    logData: {
      msg: 'Dynamic List Wrapper with a corrupt filter',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply() {
      if (fields && corruptFilterDateFieldSlugs) {
        corruptFilterDateFieldSlugs.forEach((slug: string) => {
          // eslint-disable-next-line webflow/no-delete
          delete fields[slug];
        });
      }
      if (fields && Object.keys(fields).length === 0) {
        cleanUpFilter(dynData);
      }
    },
  };
};

const isDate = (field: {id: string; slug: string; type: string}) =>
  field.type === 'Date';
const getSlug = (field: {id: string; slug: string; type: string}) => field.slug;

const partOf = (array: Array<any | string>) => (value: any) =>
  array.includes(value);

const cleanUpFilter = (dynData: any) => {
  // eslint-disable-next-line webflow/no-delete
  delete dynData.query.fields;
  if (Object.keys(dynData.query).length === 0) {
    // eslint-disable-next-line webflow/no-delete
    delete dynData.query;
  }
};
